/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useApolloClient } from '@apollo/client';
import { ConfirmModal } from '@coursera-web/cds-labs';
import _ from 'lodash';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import { useToast } from '@coursera/cds-core';
import { SuccessIcon } from '@coursera/cds-icons';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import SettingsServerActionCreators from 'bundles/account-settings/actions/SettingsServerActionCreators';
import CdsCreditCardSafetyNotice from 'bundles/account-settings/components/sections/payment-method/CdsCreditCardSafetyNotice';
import CurrentWallet from 'bundles/account-settings/components/sections/payment-method/CurrentWallet';
import useSubscriptions from 'bundles/account-settings/components/sections/payment-method/useSubscriptions';
import { getActivePaymentType } from 'bundles/account-settings/components/sections/payment-method/walletHelpers';
import { Sections, UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { PaymentWallet } from 'bundles/account-settings/types/Payment';
import { useDeletePaymentMethod } from 'bundles/account-settings/utils/useDeletePaymentMethod';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import { STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS } from 'bundles/payments-common/common/constants';

import _t from 'i18n!nls/account-settings';

type ViewCurrentCardProps = {
  wallets?: PaymentWallet[] | null;
  showDeleteCreditCardModal?: boolean;
  updateStatusDelete: {
    state: string;
  };
  activeWalletId?: number | null;
  headingRef?: React.RefObject<HTMLParagraphElement>;
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  walletList: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
};

const ViewCurrentCard = ({
  wallets,
  showDeleteCreditCardModal,
  updateStatusDelete,
  activeWalletId,
  headingRef,
}: ViewCurrentCardProps) => {
  const subscriptions = useSubscriptions();
  const { deletePaymentMethod } = useDeletePaymentMethod();
  const { show: showToast } = useToast();
  const client = useApolloClient();
  const activeWallet = wallets?.find((w) => w.id === activeWalletId);
  const { paymentProcessorId } = activeWallet || {};
  const userId = user.get().id;

  const handleCancelDeleteCard = () => {
    SettingsActionCreators.hideDeleteCreditCardModal();
  };

  const handleConfirmDeleteCard = React.useMemo(
    () =>
      _.debounce(
        () => {
          if (!activeWalletId) {
            return;
          }

          const onDeleteSuccess = () =>
            SettingsActionCreators.refetchPaymentData(userId, client).then(() => {
              SettingsActionCreators.hideDeleteCreditCardModal();
              SettingsActionCreators.resetSection(Sections.CREDIT_CARD_DELETE);
              // Delay so the DOM settles after refetch + modal close re-renders;
              // otherwise screen readers don't detect the role="alert" as new content.
              setTimeout(() => {
                showToast({
                  message: _t('Payment card successfully removed'),
                  role: 'alert',
                  durationMs: 0,
                  prefixSlot: <SuccessIcon color="success" />,
                });
              }, 500);
            });

          if (STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS.includes(paymentProcessorId || '')) {
            deletePaymentMethod({ paymentMethodId: activeWalletId.toString() })
              .then(() => onDeleteSuccess())
              .catch(() => {
                SettingsActionCreators.hideDeleteCreditCardModal();
                SettingsServerActionCreators.updateFailed(Sections.CREDIT_CARD_DELETE);
              });
          } else {
            SettingsActionCreators.deleteCreditCard(activeWalletId).then(() => onDeleteSuccess());
          }
        },
        300,
        { leading: true, trailing: false }
      ),
    [activeWalletId, paymentProcessorId, deletePaymentMethod, showToast, userId, client]
  );

  const getCurrentWalletStatus = (walletId: number) => {
    return walletId === activeWalletId ? updateStatusDelete : { state: UpdateStates.IDLE };
  };

  const activeSubscriptions = subscriptions.filter((subscription: SubscriptionsV1) =>
    ['ACTIVE', 'FREE_TRIAL', 'PAST_DUE'].includes(subscription.status || '')
  );

  return (
    <div css={styles.container}>
      <CdsCreditCardSafetyNotice />
      <div css={styles.walletList}>
        {wallets?.map((wallet) => (
          <CurrentWallet
            key={wallet.id}
            updateStatusDelete={getCurrentWalletStatus(wallet.id)}
            wallet={wallet}
            activeWalletId={activeWalletId}
            subscriptions={activeSubscriptions}
          />
        ))}
      </div>

      <ConfirmModal
        open={!!showDeleteCreditCardModal}
        acceptLabel={_t('Delete')}
        onAccept={handleConfirmDeleteCard}
        onCancel={handleCancelDeleteCard}
        returnFocusRef={headingRef}
      >
        <div>
          <FormattedMessage
            message={_t('Are you sure you want to delete your {paymentType}?')}
            paymentType={getActivePaymentType(paymentProcessorId)}
          />
          {subscriptions?.length !== 0 && (
            <p>
              (
              {_t(
                `This action will unsubscribe you from active subscriptions that use this card.
                  To avoid this, please edit your card information instead`
              )}
              )
            </p>
          )}
        </div>
      </ConfirmModal>
    </div>
  );
};

export default ViewCurrentCard;
